import { Fragment, type ReactNode } from 'react'
import { regexDePalavraInteira } from '@crm/shared'

/**
 * Texto de mensagem com a formatação do WhatsApp aplicada.
 *
 * Sem isto o balão mostra os asteriscos crus. Passou a incomodar quando toda
 * mensagem nossa passou a sair assinada com `*Nome*`, mas o problema é mais
 * velho: cliente que escreve `*urgente*` sempre apareceu aqui com os símbolos
 * à mostra, e quem lê tem de traduzir de cabeça o que o celular dele mostra
 * pronto.
 *
 * O texto continua sendo texto: cada trecho vira um `<span>` com classe, nunca
 * HTML montado a partir do que o cliente mandou. Mensagem de entrada é dado de
 * fora, e dado de fora não vira marcação.
 */

const CLASSE_DO_MARCADOR: Record<string, string> = {
  '*': 'font-semibold',
  _: 'italic',
  '~': 'line-through',
  '`': 'rounded bg-black/10 px-1 font-mono text-[0.9em]',
}

/**
 * As mesmas bordas que o WhatsApp exige: o marcador não vale no meio de uma
 * palavra (`a*b*c` fica como está), o trecho não pode começar nem terminar com
 * espaço, e não atravessa linha.
 */
const FORMATO = /(?<![\p{L}\p{N}])([*_~`])(?!\s)((?:(?!\1)[^\n])+?)(?<![\s])\1(?![\p{L}\p{N}])/gu

export function TextoWhatsApp({ texto, realce }: { texto: string; realce?: string | null }) {
  const partes = formatar(texto)
  if (!realce?.trim()) return <>{partes}</>

  /*
    O destaque entra depois da formatação, e só nos pedaços de texto puro.

    Feito antes, o negrito e o itálico do WhatsApp deixariam de ser
    reconhecidos, porque os asteriscos estariam partidos ao meio pelo realce.
  */
  const regex = regexDePalavraInteira(realce)
  return (
    <>
      {partes.map((parte, i) =>
        typeof parte === 'string' ? (
          <Fragment key={`realce-${i}`}>{realcar(parte, regex)}</Fragment>
        ) : (
          parte
        ),
      )}
    </>
  )
}

/** Envolve cada vez que a palavra aparece, deixando o resto intacto. */
function realcar(texto: string, regex: RegExp): ReactNode[] {
  const pedacos: ReactNode[] = []
  let fim = 0
  let chave = 0

  for (const achado of texto.matchAll(regex)) {
    const inicio = achado.index
    if (inicio > fim) pedacos.push(texto.slice(fim, inicio))
    pedacos.push(
      <mark key={chave++} className="rounded bg-amber-200 px-0.5 text-inherit">
        {achado[0]}
      </mark>,
    )
    fim = inicio + achado[0].length
  }

  if (fim < texto.length) pedacos.push(texto.slice(fim))
  return pedacos
}

function formatar(texto: string): ReactNode[] {
  const partes: ReactNode[] = []
  let fim = 0
  let chave = 0

  for (const achado of texto.matchAll(FORMATO)) {
    const inicio = achado.index
    if (inicio > fim) partes.push(texto.slice(fim, inicio))

    const marcador = achado[1] as string
    partes.push(
      <span key={chave++} className={CLASSE_DO_MARCADOR[marcador]}>
        {achado[2]}
      </span>,
    )
    fim = inicio + achado[0].length
  }

  if (fim < texto.length) partes.push(texto.slice(fim))
  return partes
}
